import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { listJobs } from '../api/jobs';
import { UnauthorizedError } from '../api/client';
import type { Job, JobStatus } from '../types';
import { Spinner, ErrorBanner, StatusBadge, toMessage } from '../components/common';

const STATUSES: JobStatus[] = [
  'DRAFT',
  'QUOTE_SENT',
  'PROPOSAL_SIGNED',
  'AWAITING_DOCS',
  'REVIEW',
  'QUERIES_SENT',
  'QUERIES_ANSWERED',
];

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  QUOTE_SENT: 'Quote sent',
  PROPOSAL_SIGNED: 'Proposal signed',
  AWAITING_DOCS: 'Waiting for documents',
  REVIEW: 'Review',
  QUERIES_SENT: 'Queries sent',
  QUERIES_ANSWERED: 'Queries answered',
};

type SortKey = 'entityName' | 'engagementType' | 'contact' | 'owner' | 'fee' | 'status' | 'createdAt';

function sortValue(j: Job, key: SortKey): string | number {
  switch (key) {
    case 'entityName': return (j.entityName ?? '').toLowerCase();
    case 'engagementType': return (j.engagementType ?? '').toLowerCase();
    case 'contact': return (j.contact?.name ?? '').toLowerCase();
    // Unassigned sorts last rather than first, where it is easier to spot.
    case 'owner': return (j.ownerName || '\uffff').toLowerCase();
    case 'fee': return typeof j.feeQuotedExGst === 'number' ? j.feeQuotedExGst : -1;
    case 'status': return j.status ?? '';
    case 'createdAt': return j.createdAt ?? '';
  }
}

export function Jobs() {
  const navigate = useNavigate();
  // Deleting a job navigates here with what was actually removed, so the confirmation
  // is evidence rather than a reassuring sentence.
  const [params, setParams] = useSearchParams();
  const deleted = params.get('deleted');
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [filter, setFilter] = useState<JobStatus | ''>('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setJobs(null);
    setError(null);
    listJobs(filter || undefined)
      .then((res) => {
        if (!cancelled) setJobs(res.jobs ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof UnauthorizedError) {
          navigate('/signin', { replace: true });
          return;
        }
        setError(toMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [filter, navigate]);

  const visible = useMemo(() => {
    if (!jobs) return null;
    const q = search.trim().toLowerCase();
    const filtered = q
      ? jobs.filter(
          (j) =>
            (j.entityName ?? '').toLowerCase().includes(q) ||
            (j.contact?.name ?? '').toLowerCase().includes(q),
        )
      : jobs;
    const sorted = [...filtered].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [jobs, search, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'createdAt' || key === 'fee' ? 'desc' : 'asc');
    }
  }

  const cols: { key: SortKey; label: string; col: string }[] = [
    { key: 'entityName', label: 'Entity', col: 'col-entity' },
    { key: 'engagementType', label: 'Engagement', col: 'col-engagement' },
    { key: 'contact', label: 'Contact', col: 'col-contact' },
    { key: 'owner', label: 'Owner', col: 'col-owner' },
    { key: 'fee', label: 'Fee (ex GST)', col: 'col-fee' },
    { key: 'status', label: 'Status', col: 'col-status' },
    { key: 'createdAt', label: 'Created', col: 'col-created' },
  ];

  return (
    <>
      <div className="toolbar">
        <h1>Jobs</h1>
        <Link className="btn" to="/jobs/new">
          New job
        </Link>
      </div>

      <div className="jobs-controls">
        <div className="field" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
          <label htmlFor="job-search">Search by client name</label>
          <input
            id="job-search"
            type="search"
            placeholder="Entity or contact name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="field" style={{ maxWidth: 240, marginBottom: 0 }}>
          <label htmlFor="status-filter">Filter by status</label>
          <select id="status-filter" value={filter} onChange={(e) => setFilter(e.target.value as JobStatus | '')}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s] ?? s.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
        {/* Sort control for narrow screens (the table headers are hidden there). */}
        <div className="field sort-mobile" style={{ maxWidth: 240, marginBottom: 0 }}>
          <label htmlFor="sort-mobile">Sort by</label>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <select id="sort-mobile" value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)} style={{ flex: 1 }}>
              {cols.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <button type="button" className="btn-secondary" onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))} title="Toggle direction">
              {sortDir === 'asc' ? '▲' : '▼'}
            </button>
          </div>
        </div>
      </div>

      {deleted ? (
        <div className="notice">
          <strong>{deleted} deleted.</strong> Removed {params.get('items') ?? '0'} records and{' '}
          {params.get('files') ?? '0'} stored files. Any Xero invoice for this job still has to be
          voided in Xero.
          <button
            type="button"
            className="linklike"
            style={{ marginLeft: '0.6rem' }}
            onClick={() => setParams({}, { replace: true })}
          >
            Dismiss
          </button>
        </div>
      ) : null}
      {error ? <ErrorBanner message={error} /> : null}

      {visible === null && !error ? (
        <Spinner label="Loading jobs." />
      ) : visible && visible.length === 0 ? (
        <div className="card">
          <p className="muted">
            {jobs && jobs.length > 0 ? 'No jobs match your search.' : 'No jobs yet. Create the first one to replace the intake spreadsheet.'}
          </p>
        </div>
      ) : visible ? (
        <>
          <p className="muted list-count">
            Showing {visible.length} of {jobs?.length ?? 0} job{(jobs?.length ?? 0) === 1 ? '' : 's'}
          </p>
          <div className="card jobs-table-wrap" style={{ overflowX: 'auto' }}>
            <table className="jobs-table">
              <thead>
                <tr>
                  {cols.map((c) => (
                    <th
                      key={c.key}
                      className={c.col}
                      aria-sort={sortKey === c.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                      <button type="button" className="sort-th" onClick={() => toggleSort(c.key)}>
                        {c.label}
                        <span className="sort-ind">{sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((j) => (
                  <tr key={j.jobId} style={{ cursor: 'pointer' }} onClick={() => navigate(`/jobs/${j.jobId}`)}>
                    <td className="col-entity" title={j.entityName}>
                      <Link to={`/jobs/${j.jobId}`}>{j.entityName}</Link>
                    </td>
                    <td className="col-engagement" title={j.engagementType}>{j.engagementType}</td>
                    <td className="col-contact" title={j.contact?.name}>{j.contact?.name}</td>
                    <td className="col-owner" title={j.ownerName || 'No owner set'}>
                      {j.ownerName || <span className="muted">Unassigned</span>}
                    </td>
                    <td className="col-fee">{j.feeQuotedExGst != null ? `$${j.feeQuotedExGst.toLocaleString()}` : ''}</td>
                    <td className="col-status">
                      <StatusBadge status={j.status} />
                    </td>
                    <td className="col-created muted">
                      {new Date(j.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Stacked cards for narrow screens (same data, sorted/filtered). */}
          <div className="jobs-cards">
            {visible.map((j) => (
              <Link key={j.jobId} to={`/jobs/${j.jobId}`} className="card job-card">
                <div className="job-card-head">
                  <span className="job-card-title">{j.entityName}</span>
                  <StatusBadge status={j.status} />
                </div>
                <div className="job-card-fee">
                  {j.feeQuotedExGst != null ? `$${j.feeQuotedExGst.toLocaleString()} ex GST` : 'Fee not set'}
                </div>
                <dl className="job-card-meta">
                  <div><dt>Engagement</dt><dd>{j.engagementType}</dd></div>
                  <div><dt>Contact</dt><dd>{j.contact?.name || '—'}</dd></div>
                  <div><dt>Owner</dt><dd>{j.ownerName || 'Unassigned'}</dd></div>
                  <div><dt>Created</dt><dd>{new Date(j.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</dd></div>
                </dl>
              </Link>
            ))}
          </div>
        </>
      ) : null}
    </>
  );
}
